import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { Badge } from '../components/ui/badge';
import { api } from '../api/feedy';

function FieldRow({ f }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-1.5 last:border-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium">{f.name}</code>
          {f.required && <Badge variant="destructive">required</Badge>}
          {!f.required && f.recommended && <Badge variant="warning">recommended</Badge>}
          {f.auto && <Badge variant="secondary">auto</Badge>}
          {f.multi && <Badge variant="outline">repeatable</Badge>}
        </div>
        {f.description && <p className="mt-1 text-xs text-muted-foreground">{f.description}</p>}
      </div>
      {f.example && <span className="shrink-0 text-xs text-muted-foreground italic">e.g. {f.example}</span>}
    </div>
  );
}

export default function Channels() {
  const [channels, setChannels] = useState([]);

  useEffect(() => { api.channels.list().then(setChannels).catch(() => {}); }, []);

  return (
    <AppLayout title="Channels" description="Field specifications for each output channel">
      <div className="space-y-3">
        {channels.map((ch) => {
          const groups = ch.fields.reduce((acc, f) => {
            (acc[f.group] = acc[f.group] || []).push(f);
            return acc;
          }, {});
          const required = ch.fields.filter((f) => f.required).length;
          const auto = ch.fields.filter((f) => f.auto).length;
          return (
            <details key={ch.key} className="group rounded-lg border border-border bg-card">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
                  <div className="min-w-0">
                    <h2 className="truncate text-sm font-semibold">{ch.label}</h2>
                    <p className="text-xs text-muted-foreground">
                      {ch.fields.length} fields · {required} required{auto ? ` · ${auto} generated` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  {ch.formats.map((fmt) => <Badge key={fmt} variant="secondary">{fmt.toUpperCase()}</Badge>)}
                </div>
              </summary>
              <div className="border-t border-border px-4 py-3">
                {Object.entries(groups).map(([group, fields]) => (
                  <div key={group} className="mb-3 last:mb-0">
                    <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group}</h3>
                    {fields.map((f) => <FieldRow key={f.name} f={f} />)}
                  </div>
                ))}
              </div>
            </details>
          );
        })}
      </div>
    </AppLayout>
  );
}
